import type React from "react"
import { useState, useEffect } from "react"
import { Modal, Button, Form } from "react-bootstrap"
import Select from "react-select"

interface ExtractorModalProps {
  show: boolean
  onHide: () => void
  onSubmit: (extractorType: string, parameter: string) => void
  extractorOptions: any[]
  pdfParserAppData?: any
}

const ExtractorModal: React.FC<ExtractorModalProps> = ({ show, onHide, onSubmit, extractorOptions, pdfParserAppData }) => {
  const [extractorType, setExtractorType] = useState("")
  const [parameter, setParameter] = useState("")
  const [parameterLabel, setParameterLabel] = useState("Parameter")
  const [parameterPlaceholder, setParameterPlaceholder] = useState("Enter parameter")

  // Helper to get parameters for a selected extractor
  const getExtractorParameters = (extractorName: string) => {
    if (!pdfParserAppData || !Array.isArray(pdfParserAppData.extractors)) return [];
    const extractor = pdfParserAppData.extractors.find((e: any) => e.name === extractorName);
    return extractor && Array.isArray(extractor.parameters) ? extractor.parameters : [];
  };

  // Reset form when modal is opened
  useEffect(() => {
    if (!show ) {
      setExtractorType("")
      setParameter("")
    }
  }, [show])

  // Update parameter label based on extractor type
  useEffect(() => {
    if (extractorType === "nthWord") {
      setParameterLabel("Index (starts 1)")
      setParameterPlaceholder("Enter index number")
    } else if (extractorType === "nthWordSize") {
      setParameterLabel("Index (starts 1)")
      setParameterPlaceholder("Enter index number")
    } else if (extractorType === "nextWordAfter") {
      setParameterLabel("Previous Word/Phrase")
      setParameterPlaceholder("Enter word or phrase")
    } else if (extractorType === "wordsBetween") {
      setParameterLabel("Start and End Words")
      setParameterPlaceholder("Enter start,end")
    } else {
      setParameterLabel("Parameter")
      setParameterPlaceholder("Enter parameter")
    }
  }, [extractorType])

  const handleSubmit = () => {
    if (!extractorType) {
      // Show error or validation message
      return
    }

    onSubmit(extractorType, parameter)

    onHide()
  }

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Add Extractor</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Extractor Name</Form.Label>
            <Select
              value={extractorOptions.find((option) => option.value === extractorType)}
              options={extractorOptions}
              onChange={(selected) => setExtractorType(selected?.value || "")}
              placeholder="Select Extractor"
              isClearable
              isSearchable
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
                control: (provided) => ({
                  ...provided,
                  boxShadow: "none",
                }),
              }}
            />
          </Form.Group>

          {/* Dynamic parameter fields based on extractor type */}
          {extractorType && getExtractorParameters(extractorType).map((param: any, index: number) => (
            <Form.Group key={index} className="mb-3">
              <Form.Label>{param.label}</Form.Label>
              <Form.Control
                type={param.label.toLowerCase().includes('index') ? 'number' : 'text'}
                value={parameter.split(",")[index] || ""}
                onChange={(e) => {
                  const currentParams = parameter.split(",")
                  currentParams[index] = e.target.value
                  setParameter(currentParams.join(","))
                }}
                placeholder={`Enter ${param.label.toLowerCase()}`}
                min={param.label.toLowerCase().includes('index') ? "1" : undefined}
              />
            </Form.Group>
          ))}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!extractorType}>
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ExtractorModal
